import { useEffect, useState } from "react";
import { isStorageAvailable } from "@shared/utils/storage/cache-storage";
import { IPublicExchangeManager } from "@shared/modules/exchanges/interfaces/IPublicExchangeManager";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import useUserBasedDiscountService from "@app-contexts/public/features/discounts/data/services/useUserBasedDiscountService";
import { getFormattedPrice } from "@shared/modules/financials/utils/price-utils";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";

export function usePublicExchangeManager(): IPublicExchangeManager {
  const { expireBasket, emptyBasket } = usePublicBasket();
  const { useCancelExchange, invalidateAllRepositoryQueries } =
    useUserBasedDiscountService();
  const { mutate: cancelExchangeMutation } = useCancelExchange({
    showToastOnSuccess: false,
    showToastOnError: false,
  });
  const { userProfileData } = useUserSessionAuthState();

  const [isCancellingExchange, setIsCancellingExchange] = useState(false);
  const [isExchangeActive, setIsExchangeActive] = useState(false);
  const [formattedAmount, setFormattedAmount] = useState<string | null>(null);
  const [venueData, setVenueData] = useState(null);
  const [isMembershipTicketExchange, setIsMembershipTicketExchange] =
    useState(false);
  const [isItemizedExchange, setIsItemizedExchange] = useState(false);
  const [totalTickets, setTotalTickets] = useState(null);

  // Read from localStorage and check expiry on mount and when it changes
  useEffect(() => {
    const exchangeCredit = isStorageAvailable("localStorage")
      ? localStorage.getItem("exchangeCreditData")
      : null;

    if (exchangeCredit) {
      const data = JSON.parse(exchangeCredit);
      const now = new Date();

      // Check if expired
      if (now.getTime() > data.expiry_time) {
        localStorage.removeItem("exchangeCreditData");
        setIsExchangeActive(false);
        setFormattedAmount(null);
        setVenueData(null);
        setIsMembershipTicketExchange(false);
        setIsItemizedExchange(false);
        setTotalTickets(null);
      } else {
        setIsExchangeActive(true);

        // Format the amount with currency
        const formatted = getFormattedPrice({
          inputNum: data.credit_amount,
          currency: data.currency,
          decimalPlaces: 2,
        });
        setFormattedAmount(formatted);

        setVenueData(data.venue);

        setIsMembershipTicketExchange(
          data.is_membership_ticket_exchange || false
        );
        setIsItemizedExchange(data.is_itemized || false);
        setTotalTickets(data.total_tickets || null);
      }
    } else {
      setIsExchangeActive(false);
      setFormattedAmount(null);
      setVenueData(null);
      setIsMembershipTicketExchange(false);
      setIsItemizedExchange(false);
      setTotalTickets(null);
    }
  }, []);

  const clearCredit = () => {
    if (isStorageAvailable("localStorage")) {
      localStorage.removeItem("exchangeCreditData");
    }
    setIsExchangeActive(false);
    setFormattedAmount(null);
    setVenueData(null);
    setIsMembershipTicketExchange(false);
    setIsItemizedExchange(false);
    setTotalTickets(null);
  };

  const cancelExchange = async () => {
    try {
      setIsCancellingExchange(true);

      // Call API to cancel exchange if needed
      cancelExchangeMutation({
        user_email: userProfileData?.email || "",
      });

      // On success: remove localStorage and expire basket
      clearCredit();
      await expireBasket();
      await emptyBasket();
      invalidateAllRepositoryQueries();
    } catch (error) {
      // On error: check if exchange doesn't exist, then clean up anyway
      const errorDetail =
        (error as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "";
      if (errorDetail.includes("has no itemized exchange credits to cancel")) {
        clearCredit();
        invalidateAllRepositoryQueries();
      } else {
        throw error;
      }
    } finally {
      setIsCancellingExchange(false);
    }
  };

  return {
    formattedAmount,
    isExchangeActive,
    venueData,
    isMembershipTicketExchange,
    isItemizedExchange,
    totalTickets,
    cancelExchange,
    isCancellingExchange,
  };
}
